import { buildProviderViewModel, type ProviderViewModel } from '@/models/provider';
import { buildWabunkaChargeType, type WabunkaChargeType } from '@/models/wabunka/wabunkaChargeType';
import { buildWabunkaExperienceOptionListViewModel, type WabunkaExperienceOptionViewModel } from '@/models/wabunka/wabunkaExperienceOption';
import { buildWabunkaInterpreterOptionListViewModel, type WabunkaInterpreterOptionViewModel } from '@/models/wabunka/wabunkaInterpreterOption';
import { buildWabunkaPublishStatus, type WabunkaPublishStatus } from '@/models/wabunka/wabunkaPublishStatus';
import { buildWabunkaReservationQuestionListViewModel, type WabunkaReservationQuestionViewModel } from '@/models/wabunka/wabunkaReservationQuestionAnswer';
import { calcPriceIncludingTax } from '@/utils/priceIncludingTax';
import { DEFAULT_TAX_RATE } from '@/wabunka-src/constants/enExperiences';
import { buildWabunkaCancellationPolicyTrigger, type WabunkaCancellationPolicyTrigger } from '@/wabunka-src/features/Experience/models/cancellationPolicyTrigger';
import { buildWabunkaExperienceTagListViewModel, type WabunkaExperienceTagListViewModel } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTag';

import type { WabunkaCurrencyConvertedPrice } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_currency';
import type { DurationObject, WabunkaExperience, WabunkaExperienceImage, WabunkaExperienceListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

/** 体験画像 */
export interface WabunkaExperienceImageViewModel {
  /** ID */
  id: string;
  /** オプションID */
  experienceId: string;
  /** 画像のURL */
  imageUrl: string;
  /** シーケンスID */
  sequenceId: number;
}

export type WabunkaExperienceImageListViewModel = WabunkaExperienceImageViewModel[];

// TODO wabunka-src/features/Experience/models/experience.tsのExperienceViewModelに移行する
/**
 * Wabunka 体験ビューモデル
 */
export interface WabunkaExperienceViewModel {
  /** ID */
  id: string;
  /** 体験名 */
  name: string;
  /** [Optional] 事業者 */
  provider?: ProviderViewModel;
  /** 課金タイプ */
  chargeType: WabunkaChargeType;
  /** 受入可能人数 */
  maxGuests: number;
  /** 最少予約人数 */
  minGuestsPerReservation: number;
  /** スラッグ */
  slug: string;
  /** 一人あたりの金額 */
  price: number;
  /** 参考価格（税込） */
  referencePrice: number | undefined;
  /** 参考料金の注釈文（リッチテキスト） */
  referencePriceAnnotation?: string;
  guestCompositionAnnotation?: string;
  /** カレンダーの注釈文（プレーンテキスト） */
  calendarNote?: string;
  /** 価格が公開か非公開か */
  isPublicPrice: boolean;
  /** 所要時間 */
  experienceDuration: DurationObject | undefined;
  /** キャンセルポリシーのトリガー */
  cancellationPolicyTrigger: WabunkaCancellationPolicyTrigger;
  /** キャンセルポリシーのラダー設定が有効かどうか */
  isLadderEnabled: boolean;
  /** キャンセル・変更締切（N時間前） */
  confirmedBookingLeadHours: number;
  /** 返金ありのゲストキャンセル期限（N日前） */
  refundableCancellationDaysForGuest: number;
  /** 体験オプション */
  experienceOptions: WabunkaExperienceOptionViewModel[];
  /** 通訳オプション */
  interpreterOptions: WabunkaInterpreterOptionViewModel[];
  /** 予約質問 */
  reservationQuestions: WabunkaReservationQuestionViewModel[];
  /**
   * 追加プロパティ
   */
  priceIncludingTax: number;
  /**
   * 体験画像(1枚目をサムネイルとして使用している)
   */
  images: WabunkaExperienceImageListViewModel;
  /**
   * タグ一覧
   */
  tags: WabunkaExperienceTagListViewModel;
  /** 公開状態 */
  publishStatus: WabunkaPublishStatus;
  /** 開催場所概要 */
  venueOverview: string;
  /** 開催場所詳細 */
  venueDetail: string;
  /** 開催場所の緯度（未設定の場合は undefined） */
  latitude?: number;
  /** 開催場所の経度（未設定の場合は undefined） */
  longitude?: number;
  /** 通貨換算済み価格（JPY以外の通貨が指定された場合に設定される） */
  currencyConvertedPrice?: WabunkaCurrencyConvertedPrice;
  /** 通訳手配要否（Wabunkaで通訳手配が必要か。事業者が英語対応可能な場合は不要） */
  isInterpreterNeeded: boolean;
}

/**
 * 体験詳細用ビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaExperienceViewModel} - 体験詳細用ビューモデルのデフォルト値
 */
export const createDefaultWabunkaExperienceViewModel = (): WabunkaExperienceViewModel => ({
  id: '',
  name: '',
  chargeType: '',
  maxGuests: 0,
  minGuestsPerReservation: 0,
  slug: '',
  price: 0,
  referencePrice: undefined,
  referencePriceAnnotation: undefined,
  guestCompositionAnnotation: undefined,
  calendarNote: undefined,
  isPublicPrice: true,
  experienceDuration: undefined,
  cancellationPolicyTrigger: 'RESERVATION_CONFIRMED',
  isLadderEnabled: false,
  confirmedBookingLeadHours: 0,
  refundableCancellationDaysForGuest: 0,
  experienceOptions: [],
  interpreterOptions: [],
  reservationQuestions: [],
  priceIncludingTax: 0,
  images: [],
  tags: [],
  publishStatus: 'DRAFT',
  venueOverview: '',
  venueDetail: '',
  latitude: undefined,
  longitude: undefined,
  isInterpreterNeeded: false,
});

/**
 * APIレスポンスから体験画像ビューモデルを作成する
 *
 * @param {WabunkaExperienceImage[]} res - APIレスポンス
 * @returns {WabunkaExperienceImageListViewModel} - ビューモデル
 */
export const buildWabunkaExperienceImageListViewModel = (res: WabunkaExperienceImage[]): WabunkaExperienceImageListViewModel => {
  return res
    .map((experienceImage) => ({
      id: experienceImage.id,
      experienceId: experienceImage.experienceId,
      imageUrl: experienceImage.imageUrl,
      sequenceId: experienceImage.sequenceId,
    }))
    .sort((a, b) => a.sequenceId - b.sequenceId);
};

/**
 * APIレスポンスから体験ビューモデルを作成する
 *
 * @param {WabunkaExperience} res - APIレスポンス
 * @returns {WabunkaExperienceViewModel} - ビューモデル
 */
export const buildWabunkaExperienceViewModel = (res: WabunkaExperience | undefined): WabunkaExperienceViewModel => {
  if (!res) return createDefaultWabunkaExperienceViewModel();
  return {
    id: res.id,
    name: res.name,
    maxGuests: res.maxGuests,
    minGuestsPerReservation: res.minGuestsPerReservation,
    slug: res.slug,
    chargeType: buildWabunkaChargeType(res.chargeType),
    provider: buildProviderViewModel(res.provider),
    price: res.price,
    referencePrice: res.referencePrice,
    referencePriceAnnotation: res.referencePriceAnnotation,
    guestCompositionAnnotation: res.guestCompositionAnnotation,
    calendarNote: res.calendarNote,
    isPublicPrice: res.isPublicPrice,
    experienceDuration: res.experienceDuration,
    cancellationPolicyTrigger: buildWabunkaCancellationPolicyTrigger(res.cancellationPolicyTrigger),
    isLadderEnabled: res.isLadderEnabled ?? false,
    confirmedBookingLeadHours: res.confirmedBookingLeadHours ?? 0,
    refundableCancellationDaysForGuest: res.refundableCancellationDaysForGuest,
    experienceOptions: buildWabunkaExperienceOptionListViewModel(res.experienceOptions),
    interpreterOptions: buildWabunkaInterpreterOptionListViewModel(res.experienceInterpreterOptions),
    reservationQuestions: buildWabunkaReservationQuestionListViewModel(res.experienceReservationQuestions),
    priceIncludingTax: calcPriceIncludingTax(res.price, DEFAULT_TAX_RATE),
    publishStatus: buildWabunkaPublishStatus(res.publishStatus),
    images: buildWabunkaExperienceImageListViewModel(res.images),
    tags: buildWabunkaExperienceTagListViewModel(res.tags),
    venueOverview: res.venueOverview,
    venueDetail: res.venueDetail,
    latitude: res.latitude,
    longitude: res.longitude,
    currencyConvertedPrice: res.currencyConvertedPrice,
    isInterpreterNeeded: res.isInterpreterNeeded ?? false,
  };
};

/**
 * リスト表示用のビューモデル
 */
export type WabunkaExperienceListViewModel = WabunkaExperienceViewModel[];

/**
 * リスト表示用のビューモデルを組み立てる
 */
export const buildWabunkaExperienceListViewModel = (experiences: WabunkaExperience[] | undefined): WabunkaExperienceListViewModel => {
  if (!experiences) {
    return [];
  }

  return experiences.map(buildWabunkaExperienceViewModel);
};

const sortHistoryBySlugs = (unsortedExperiences: WabunkaExperienceListViewModel, historySlugs: string[]): WabunkaExperienceViewModel[] => {
  const historyMap = new Map<string, WabunkaExperienceViewModel>();
  for (const experience of unsortedExperiences) {
    historyMap.set(experience.slug, experience);
  }

  return historySlugs.map((slug) => historyMap.get(slug)).filter((history): history is WabunkaExperienceViewModel => history !== undefined);
};

/**
 * 閲覧履歴表示用のビューモデルを組み立てる
 */
export const buildWabunkaExperienceBrowsingHistoryListViewModel = (experiences: WabunkaExperienceListResponse, historySlugs: string[]): WabunkaExperienceListViewModel => {
  if (experiences.items.length < 1) return [];

  const unsortedExperiences = experiences.items.map(buildWabunkaExperienceViewModel);
  return sortHistoryBySlugs(unsortedExperiences, historySlugs);
};
